import React from 'react'
import {
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
} from '@material-ui/core'
import {
  Check as CheckIcon,
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  ErrorOutline as ErrorOutlineIcon,
  FileDownload as FileDownloadIcon,
  Search as SearchIcon,
  Warning as WarningIcon,
} from '@material-ui/icons'
import {
  DIFF_FILTERS,
  DIFF_PAGE_SIZE,
  SWITCH_OVER_TASKS,
  TASK_STATUS,
  batchCount,
  describeSwitchOverFailure,
  filterDiffRows,
  formatCount,
  formatTime,
} from '../../utils/nmCcdMigration'

const Spinner = ({ size = 18 }) => (
  <CircularProgress size={size} thickness={4} classes={{ root: 'nmccd-migration__spinner' }} />
)

const IdentityGrid = ({ rows, plain = false }) => (
  <div className={`nmccd-migration__identity${plain ? ' nmccd-migration__identity--plain' : ''}`}>
    {rows.map(({ label, value }) => (
      <React.Fragment key={label}>
        <div className="nmccd-migration__identity-label">{label}</div>
        <div className="nmccd-migration__identity-value">{value}</div>
      </React.Fragment>
    ))}
  </div>
)

const Tile = ({ label, value, caption, variant }) => (
  <div className={`nmccd-migration__tile${variant ? ` nmccd-migration__tile--${variant}` : ''}`}>
    <div className="nmccd-migration__tile-label">{label}</div>
    <div className="nmccd-migration__tile-value">{value}</div>
    {caption ? <div className="nmccd-migration__tile-caption">{caption}</div> : null}
  </div>
)

export const ValidateTokenStep = ({
  account = {},
  environmentEditable = false,
  environments = [],
  onEnvironmentChange = () => {},
  jwt,
  onJwtChange,
  validating,
  saving = false,
  result,
  error,
  onValidate,
}) => {
  const last4 = account.nmCcdJwtTokenLast4
  const metadata = (result && result.metadata) || {}
  const locationMatches =
    Boolean(metadata.locationId) &&
    String(metadata.locationId) === String(account.nmCcdLocationId || '')
  return (
    <div>
      <div className="nmccd-migration__intro">
        Confirm the S2S credentials for this location before anything moves. Nothing is migrated
        until the last step.
      </div>
      <IdentityGrid
        rows={[
          {
            label: 'Environment',
            value: environmentEditable ? (
              <TextField
                select
                id="nmccd-migration-environment"
                value={account.environment || ''}
                onChange={(event) => onEnvironmentChange(event.target.value)}
                SelectProps={{ native: true }}
                inputProps={{ 'aria-label': 'Environment' }}
              >
                {environments.map((environment) => (
                  <option key={environment} value={environment}>
                    {environment}
                  </option>
                ))}
              </TextField>
            ) : (
              account.environment || ''
            ),
          },
          { label: 'Organization ID', value: String(account.nmCcdOrganizationId || '') },
          { label: 'Location ID', value: String(account.nmCcdLocationId || '') },
          { label: 'Saved token', value: last4 ? `••••${last4}` : 'Not configured' },
        ]}
      />
      <div className="nmccd-migration__field">
        <label className="nmccd-migration__field-label" htmlFor="nmccd-migration-jwt">
          JWT token
        </label>
        <TextField
          id="nmccd-migration-jwt"
          type="password"
          autoComplete="off"
          fullWidth
          value={jwt}
          placeholder={
            last4 ? 'Paste a new token to replace the saved one' : 'Paste the location token'
          }
          onChange={(event) => onJwtChange(event.target.value)}
        />
        {error ? (
          <div className="nmccd-migration__error" role="alert">
            {error}
          </div>
        ) : (
          <div className="nmccd-migration__helper">
            {last4
              ? `Leave blank to keep the saved token (••••${last4}). A new token replaces it once it validates.`
              : 'Paste the location token issued by NM CCD. It is saved with this configuration once it validates.'}
          </div>
        )}
      </div>
      <div className="nmccd-migration__actions">
        <Button className="primary-button" disabled={saving} onClick={onValidate}>
          {saving
            ? 'Saving…'
            : validating
            ? 'Validating…'
            : result
            ? 'Validate again'
            : 'Validate token'}
        </Button>
        {validating ? <Spinner /> : null}
      </div>
      {result ? (
        <div className="nmccd-migration__panel">
          <div className="nmccd-migration__panel-head">
            <CheckCircleIcon />
            Token accepted. Connection verified.
          </div>
          <IdentityGrid
            plain
            rows={[
              { label: 'Organization', value: metadata.organizationName || metadata.name || '' },
              {
                label: 'Location ID',
                value: (
                  <React.Fragment>
                    {String(metadata.locationId || '')}
                    {locationMatches ? (
                      <React.Fragment>
                        <CheckIcon
                          className="nmccd-migration__identity-check"
                          aria-label="Location ID matches current configuration"
                        />
                        <span className="nmccd-migration__identity-hint">
                          matches current configuration
                        </span>
                      </React.Fragment>
                    ) : null}
                  </React.Fragment>
                ),
              },
              { label: 'Address', value: metadata.address || '' },
              { label: 'POS API status', value: metadata.posApiConnectionStatus || '' },
              { label: 'Checked at', value: formatTime(result.checkedAt, 'MMM D, h:mm A') },
            ]}
          />
        </div>
      ) : null}
    </div>
  )
}

export const FetchInventoryStep = ({ account = {}, fetching, inventory, error, onFetch }) => {
  const location = String(account.nmCcdLocationId || '')
  const note = fetching
    ? 'Taking too long? Click to start the fetch again.'
    : inventory
    ? 'Inventory is current as of the fetch below.'
    : 'Read-only. Takes a few seconds.'
  const status = error
    ? error
    : fetching
    ? `Syncing S2S inventory for location ${location}…`
    : inventory
    ? `Done. ${formatCount(inventory.s2sPackageCount)} packages returned for location ${location}.`
    : ''
  return (
    <div>
      <div className="nmccd-migration__intro">
        Read the current package list from S2S for location {location}. This is a read-only fetch —
        nothing in Blaze changes.
      </div>
      <div className="nmccd-migration__actions" style={{ marginTop: 0 }}>
        <Button className="primary-button" onClick={onFetch}>
          {fetching ? 'Fetching…' : inventory || error ? 'Fetch again' : 'Fetch inventory'}
        </Button>
        <span className="nmccd-migration__note">{note}</span>
      </div>
      {fetching || inventory || error ? (
        <div className="nmccd-migration__progress">
          <LinearProgress
            variant={fetching ? 'indeterminate' : 'determinate'}
            value={inventory ? 100 : 0}
            classes={{ barColorPrimary: 'nmccd-migration__bar' }}
          />
          <div
            className={`nmccd-migration__progress-status${
              error ? ' nmccd-migration__progress-status--error' : ''
            }`}
            role={error ? 'alert' : undefined}
            aria-live={error ? undefined : 'polite'}
          >
            {status}
          </div>
        </div>
      ) : null}
      {inventory ? (
        <div className="nmccd-migration__tiles">
          <Tile label="S2S packages" value={formatCount(inventory.s2sPackageCount)} />
          <Tile label="Blaze active batches" value={formatCount(inventory.blazeBatchCount)} />
          <Tile label="Fetched at" value={formatTime(inventory.fetchedAt)} />
        </div>
      ) : null}
    </div>
  )
}

const FILTER_OPTIONS = [
  [DIFF_FILTERS.ALL, 'All'],
  [DIFF_FILTERS.MATCHED, 'Matched'],
  [DIFF_FILTERS.MISSING, 'Not in S2S'],
]

export const CompareBatchesStep = ({
  preview,
  ack,
  onAckChange,
  query,
  onQueryChange,
  filter,
  onFilterChange,
  page,
  onPageChange,
  onDownloadCsv,
}) => {
  if (!preview) return null

  const filtered = filterDiffRows(preview.rows, query, filter)
  const maxPage = Math.max(0, Math.ceil(filtered.length / DIFF_PAGE_SIZE) - 1)
  const currentPage = Math.min(page, maxPage)
  const start = currentPage * DIFF_PAGE_SIZE
  const visible = filtered.slice(start, start + DIFF_PAGE_SIZE)
  const unmatched = preview.unmatchedCount

  return (
    <div>
      <div className="nmccd-migration__tiles nmccd-migration__tiles--two">
        <Tile
          variant="matched"
          label="Matched batches"
          value={formatCount(preview.matchedCount)}
          caption="Will move to S2S tracking system."
        />
        <Tile
          variant="missing"
          label="In Blaze, missing in S2S"
          value={formatCount(unmatched)}
          caption="Will stay on BioTrack."
        />
      </div>
      {unmatched > 0 ? (
        <div className="nmccd-migration__warning">
          <div className="nmccd-migration__warning-row">
            <WarningIcon />
            <div className="nmccd-migration__warning-text">
              {`${batchCount(unmatched)} in Blaze ${
                unmatched === 1 ? 'has' : 'have'
              } no matching package in S2S. ${
                unmatched === 1 ? 'It keeps its' : 'They keep their'
              } current compliance system and must be reconciled in S2S before ${
                unmatched === 1 ? 'it' : 'they'
              } can be sold.`}
            </div>
          </div>
          <FormControlLabel
            className="nmccd-migration__ack"
            classes={{ label: 'nmccd-migration__ack-label' }}
            control={
              <Checkbox
                checked={ack}
                onChange={(event) => onAckChange(event.target.checked)}
                classes={{ checked: 'nmccd-migration__checkbox' }}
              />
            }
            label={`I understand ${batchCount(unmatched)} will not be migrated.`}
          />
        </div>
      ) : null}
      <div className="nmccd-migration__toolbar">
        <div className="nmccd-migration__search">
          <SearchIcon />
          <TextField
            value={query}
            onChange={(event) => onQueryChange(event.target.value)}
            placeholder="Search batch or product"
            fullWidth
            InputProps={{ disableUnderline: true }}
            inputProps={{ 'aria-label': 'Search batch or product' }}
            style={{ marginLeft: 6 }}
          />
        </div>
        <div className="nmccd-migration__filters" role="group" aria-label="Filter batches">
          {FILTER_OPTIONS.map(([value, label]) => (
            <button
              key={value}
              type="button"
              className={`nmccd-migration__filter${
                filter === value ? ' nmccd-migration__filter--active' : ''
              }`}
              aria-pressed={filter === value}
              onClick={() => onFilterChange(value)}
            >
              {label}
            </button>
          ))}
        </div>
        <Button className="nmccd-migration__csv" onClick={() => onDownloadCsv(filtered)}>
          <FileDownloadIcon />
          Download CSV
        </Button>
      </div>
      <div className="nmccd-migration__table">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell className="nmccd-migration__cell nmccd-migration__cell--batch">
                Batch
              </TableCell>
              <TableCell className="nmccd-migration__cell">Product</TableCell>
              <TableCell className="nmccd-migration__cell nmccd-migration__cell--qty">
                Qty
              </TableCell>
              <TableCell className="nmccd-migration__cell nmccd-migration__cell--s2s">
                S2S package
              </TableCell>
              <TableCell className="nmccd-migration__cell nmccd-migration__cell--status">
                Status
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visible.map((row, index) => (
              <TableRow key={row.batchId || `${row.batch}-${start + index}`}>
                <TableCell className="nmccd-migration__cell nmccd-migration__cell--batch">
                  {row.batch || '—'}
                </TableCell>
                <TableCell className="nmccd-migration__cell" title={row.product}>
                  {row.product}
                </TableCell>
                <TableCell className="nmccd-migration__cell nmccd-migration__cell--qty">
                  {row.qty}
                </TableCell>
                <TableCell className="nmccd-migration__cell nmccd-migration__cell--s2s">
                  {row.matched ? row.s2s : '—'}
                </TableCell>
                <TableCell className="nmccd-migration__cell nmccd-migration__cell--status">
                  <span
                    className={`nmccd-migration__badge${
                      row.matched ? '' : ' nmccd-migration__badge--missing'
                    }`}
                    title={row.reason || undefined}
                  >
                    {row.matched ? 'Matched' : 'Not in S2S'}
                  </span>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {filtered.length === 0 ? (
          <div className="nmccd-migration__empty">No batches match this filter.</div>
        ) : null}
        <TablePagination
          component="div"
          classes={{ root: 'nmccd-migration__pager-root', toolbar: 'nmccd-migration__pager' }}
          count={filtered.length}
          rowsPerPage={DIFF_PAGE_SIZE}
          rowsPerPageOptions={[]}
          page={currentPage}
          onChangePage={(_, nextPage) => onPageChange(nextPage)}
          onChangeRowsPerPage={() => {}}
          labelDisplayedRows={({ from, to, count }) =>
            `${formatCount(from)}–${formatCount(to)} of ${formatCount(count)}`
          }
          backIconButtonProps={{ 'aria-label': 'Previous page' }}
          nextIconButtonProps={{ 'aria-label': 'Next page' }}
        />
      </div>
      {preview.truncated ? (
        <div className="nmccd-migration__truncated">
          Only the first {formatCount(preview.rows.length)} batches are listed.
        </div>
      ) : null}
    </div>
  )
}

const TaskGlyph = ({ status, index }) => {
  if (status === TASK_STATUS.RUNNING) {
    return (
      <span className="nmccd-migration__glyph nmccd-migration__glyph--running">
        <Spinner size={22} />
      </span>
    )
  }
  if (status === TASK_STATUS.DONE) {
    return (
      <span className="nmccd-migration__glyph nmccd-migration__glyph--done">
        <CheckIcon />
      </span>
    )
  }
  if (status === TASK_STATUS.FAILED) {
    return (
      <span className="nmccd-migration__glyph nmccd-migration__glyph--failed">
        <CloseIcon />
      </span>
    )
  }
  return <span className="nmccd-migration__glyph">{index + 1}</span>
}

export const SwitchOverStep = ({
  shop = {},
  account = {},
  preview,
  tasks,
  running,
  done,
  failure,
  mapResult,
}) => {
  const matched = preview ? preview.matchedCount : 0
  const unmatched = preview ? preview.unmatchedCount : 0
  const started = running || done || Boolean(failure)
  const copy = {
    map: {
      title: `Move ${batchCount(matched)} to S2S`,
      detail: `Compliance System changes from BIOTRACK to NM_CCD on every matched batch. ${formatCount(
        unmatched,
      )} unmatched ${unmatched === 1 ? 'batch is' : 'batches are'} left on BIOTRACK.`,
    },
    enable: {
      title: 'Enable NM CCD and sync inventory',
      detail: `Organization ${account.nmCcdOrganizationId || ''}, location ${
        account.nmCcdLocationId || ''
      }, ${
        account.environment || ''
      }. Enabled and Enable Sync both switch on, then the S2S inventory is synced into Blaze.`,
    },
    disable: {
      title: 'Disable BioTrack for this shop',
      detail:
        'The BioTrack location is switched off, along with its delivery reporting and sync. Credentials are kept.',
    },
  }

  if (!started) {
    return (
      <div>
        <div className="nmccd-migration__heading">What will change</div>
        <div className="nmccd-migration__subheading">
          Runs as three steps against {shop.name || 'this shop'}. Each step is safe to run again.
        </div>
        <div className="nmccd-migration__list">
          {SWITCH_OVER_TASKS.map((task, index) => (
            <div className="nmccd-migration__item" key={task.key}>
              <span className="nmccd-migration__glyph">{index + 1}</span>
              <div className="nmccd-migration__item-body">
                <div className="nmccd-migration__item-title">{copy[task.key].title}</div>
                <div className="nmccd-migration__item-detail">{copy[task.key].detail}</div>
              </div>
            </div>
          ))}
        </div>
        <div className="nmccd-migration__closing-note">
          This cannot be undone from this screen. Reverting requires support.
        </div>
      </div>
    )
  }

  const summary = failure ? describeSwitchOverFailure(tasks) : null
  const mapped = mapResult ? mapResult.mappedCount : matched
  return (
    <div>
      <div className="nmccd-migration__heading" style={{ marginBottom: 20 }}>
        {done ? 'Switch over' : failure ? 'Switch over stopped' : 'Switching over…'}
      </div>
      <div className="nmccd-migration__list" aria-live="polite">
        {tasks.map((task, index) => (
          <div className="nmccd-migration__item" key={task.key}>
            <TaskGlyph status={task.status} index={index} />
            <div className="nmccd-migration__item-body">
              <div className="nmccd-migration__item-title">{copy[task.key].title}</div>
              <div
                className={`nmccd-migration__item-detail${
                  task.status === TASK_STATUS.FAILED ? ' nmccd-migration__item-detail--error' : ''
                }`}
              >
                {task.detail}
              </div>
              {task.key === 'map' && task.status === TASK_STATUS.RUNNING ? (
                <div className="nmccd-migration__item-bar">
                  <LinearProgress classes={{ barColorPrimary: 'nmccd-migration__bar' }} />
                </div>
              ) : null}
            </div>
          </div>
        ))}
      </div>
      {done ? (
        <div className="nmccd-migration__result" role="status">
          <div className="nmccd-migration__result-head">
            <CheckCircleIcon />
            Switch over complete.
          </div>
          <div className="nmccd-migration__result-body">
            {`${batchCount(mapped)} ${
              mapped === 1 ? 'is' : 'are'
            } on S2S and NM CCD sync is running. ${batchCount(
              unmatched,
            )} stayed on BioTrack — reconcile ${
              unmatched === 1 ? 'it' : 'them'
            } in S2S, then run the migration again.`}
          </div>
        </div>
      ) : null}
      {failure ? (
        <div className="nmccd-migration__result nmccd-migration__result--failed" role="alert">
          <div className="nmccd-migration__result-head">
            <ErrorOutlineIcon />
            {summary.title}
          </div>
          <div className="nmccd-migration__result-body">
            {failure.message}
            <br />
            Applied: {summary.applied.length ? summary.applied.join(', ') : 'nothing'}. Not applied:{' '}
            {summary.notApplied.join(', ')}.
            <br />
            {summary.notApplied.length === 1 && summary.notApplied[0] === 'Disable BioTrack'
              ? 'NM CCD is already active for this shop. Fix the issue and click Try again, or disable BioTrack from the BioTrack Locations tab.'
              : 'Fix the issue and click Try again. Completed steps are skipped.'}
          </div>
        </div>
      ) : null}
    </div>
  )
}



// WEBPACK FOOTER //
// ./src/components/Compliance/NmCcdMigrationSteps.js